import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { useUserPreferences } from '@/shared/hooks/useUserPreferences';
import { cn } from '@/shared/lib/utils';
import { isI18nEnabled } from '@/shared/lib/config';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from '@/i18n';
import { FlagBR, FlagES, FlagGB } from '@/i18n/flags';

const LANGUAGE_OPTIONS: {
  value: SupportedLanguage;
  labelKey: string;
  Flag: ComponentType<{ className?: string }>;
}[] = [
  { value: DEFAULT_LANGUAGE, labelKey: 'language.portuguese', Flag: FlagBR },
  { value: 'es-ES', labelKey: 'language.spanish', Flag: FlagES },
  { value: 'en-US', labelKey: 'language.english', Flag: FlagGB },
];

export function LanguageMenu() {
  const { t, i18n } = useTranslation('common');
  const { setLanguage } = useUserPreferences();

  // Guarda no componente, e nao nos call sites: o menu aparece em dois pontos do
  // AppSidebar (expandido e recolhido) e um call site novo herda a flag de graca.
  if (!isI18nEnabled()) return null;

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={t('language.tooltip')}
              className="flex items-center justify-center p-2 rounded-lg hover:bg-sidebar-accent/50 transition-colors"
            >
              <Globe className="h-5 w-5 text-sidebar-foreground" />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{t('language.tooltip')}</p>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent side="top" align="start">
        {LANGUAGE_OPTIONS.map(({ value, labelKey, Flag }) => (
          <DropdownMenuItem
            key={value}
            onClick={() => setLanguage(value)}
            className={cn(
              i18n.language === value &&
                'bg-primary text-primary-foreground focus:bg-primary focus:text-primary-foreground'
            )}
          >
            <Flag className="mr-2 h-3.5 w-5 rounded-sm" />
            {t(labelKey)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
